import { PaymentIntent } from '@stripe/stripe-js'
import { IPaymentIntent } from '@stripe/terminal-js'
import { ApiTender, ApiTransaction } from '@vend/types'
import VendNumber, { vn } from 'vend-number'
import { getPaymentMethodType } from './paymentIntent'

/**
 * Create a transaction object to resolve the sale when we cannot fetch a transaction for an already captured payment
 * @param intent the intent from the terminal once the payment is collected
 * @param mainTenderAmount amount of the sale, will be the amount of main SALE tender
 * @return {ApiTransaction} a transaction object mimicking the transaction we should receive from capture API endpoint, with some missing fields that we do no know
 */
export const createCapturedTransaction = ({
  intent,
  registerId,
  transactionId,
  mainTenderAmount,
  currency,
  retailerPaymentTypeId,
  paymentTypeId,
}: {
  intent: IPaymentIntent | PaymentIntent
  registerId: string
  transactionId: string
  mainTenderAmount: VendNumber
  currency: string
  retailerPaymentTypeId: string
  paymentTypeId: number
}): ApiTransaction => {
  const created_at = new Date().toISOString()
  const tenders: ApiTender[] = [
    {
      external_transaction_id: intent.id,
      currency,
      amount: mainTenderAmount.toNumber(),
      type: 'SALE',
      status: 'APPROVED',
      card_details: {
        card: {},
      },
      emv: {
        tags: null,
      },
      provider_fields: {
        payment_method_type: getPaymentMethodType(intent),
      },
      id: '', // we will not know the tender id as it is generated at Swiper, and it is not used for persisting
      created_at,
    },
  ]

  const mainTenderAmountInCents = mainTenderAmount.times(100)
  // the amount in intent is integer cents
  const collectedAmountInCents = vn(intent.amount)
  const tipAmountInCents = collectedAmountInCents.minus(mainTenderAmountInCents)
  // the minimum tip amount accepted by Stripe is 1 cent
  if (tipAmountInCents.greaterThanOrEqualTo(1)) {
    const tipTender: ApiTender = {
      external_transaction_id: intent.id,
      currency,
      amount: collectedAmountInCents
        .minus(mainTenderAmountInCents)
        .div(100)
        .toNumber(),
      type: 'TIP',
      status: 'APPROVED',
      card_details: {
        card: {},
      },
      emv: {
        tags: null,
      },
      provider_fields: {},
      id: '', // we will not know the tender id as it is generated at Swiper, and it is not used for persisting
      created_at,
    }
    tenders.push(tipTender)
  }

  return {
    tenders,
    register_id: registerId,
    register_sale_payment_id: transactionId,
    retailer_payment_type_id: retailerPaymentTypeId,
    payment_type_id: paymentTypeId,
    status: 'COMPLETED',
    provider_fields: {},
    id: 'missing_in_swiper', // this will be transformed into `source_id` when passing through the register sale for persisting
  }
}
